import { Box } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@reflex/ui/components/ui/hover-card';
import { cn } from '@reflex/ui/lib/utils';
import { RESOURCE_PREVIEW_HOVER_CONTENT_CLASS } from '@reflex/ui/components/resource-preview/ResourcePreviewCard';
import { ResourcePreview } from './ResourcePreview';
import { useResourcePreviewRegistration } from './useResourcePreviewRegistration';

export interface ResourceChipProps {
  /** Stable kind discriminator, e.g. `'personas'`. */
  kind: string;
  /** Resource id passed to the preview component. */
  id: string;
  /** Display name shown on the chip. Falls back to the full id (rendered in monospace). */
  name?: string;
  /** Optional class name applied to the chip badge. */
  className?: string;
  /** Test id for play-function assertions. */
  'data-testid'?: string;
}

/**
 * Compact, inline reference to any resource. Hovering opens a generic
 * preview popover for the kind. Use anywhere a blueprint, persona, MCP
 * server, agent, or flow is referenced inline (cards, sentences, lists).
 */
export function ResourceChip({
  kind,
  id,
  name,
  className,
  'data-testid': dataTestId,
}: ResourceChipProps) {
  const registration = useResourcePreviewRegistration(kind);
  const Icon = registration?.icon ?? Box;
  const displayName = name ?? id;
  const isIdFallback = !name;
  const ariaKindLabel = registration?.label ?? kind;

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={`Preview ${ariaKindLabel} ${displayName}`}
          data-testid={dataTestId ?? `resource-chip-${kind}-${id}`}
          title={displayName}
          className="inline-flex items-center rounded-md align-middle transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Badge
            variant="outline"
            className={cn('cursor-default text-[10px] font-medium px-1.5 py-0', className)}
          >
            <Icon className="h-2.5 w-2.5 mr-0.5" aria-hidden="true" />
            <span className={cn('truncate max-w-[16rem]', isIdFallback && 'font-mono')}>
              {displayName}
            </span>
          </Badge>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
        aria-label={`${ariaKindLabel} ${displayName}`}
      >
        <ResourcePreview kind={kind} id={id} fallbackName={displayName} />
      </HoverCardContent>
    </HoverCard>
  );
}
